import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import type { Client, HeatmapData, LfFullScan, LfGridPoint, LfRanking, LfScan } from '../types';
import { deleteLfScan, getLfScan } from '../lib/api';
import { useIsMobile } from '../lib/useIsMobile';
import {
  GBIBadge, GBIBtn, GBICard, GBILabel, Icon, T, rankColor, sharedStyles,
} from './Shared';
import type { NavId } from './Sidebar';

type TabId = 'map';

interface HeatmapViewProps {
  data: HeatmapData;
  lfScans?: LfScan[];
  onRefreshLfAll?: () => Promise<void>;
  onRefreshLfKeyword?: (keyword: string) => Promise<void>;
  // Used to jump to the AI Analysis tab from inside the heatmap header.
  onNav?: (id: NavId) => void;
  readOnly?: boolean;
}

interface ClickedPoint {
  index: number;
  lat: number;
  lng: number;
}

export function HeatmapView({
  data,
  lfScans = [],
  onRefreshLfAll,
  onRefreshLfKeyword,
  onNav,
  readOnly = false,
}: HeatmapViewProps) {
  const { client } = data;
  const hasScans = lfScans.length > 0;
  const isMobile = useIsMobile();

  // ── State ───────────────────────────────────────────────────────────────
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(
    hasScans ? lfScans[0].keyword : null
  );
  const [fullScan, setFullScan] = useState<LfFullScan | null>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('map');
  const [clickedPoint, setClickedPoint] = useState<ClickedPoint | null>(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshingKw, setRefreshingKw] = useState<string | null>(null);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);

  // Keep selectedKeyword in sync if the list changes (e.g. after refresh).
  useEffect(() => {
    if (!hasScans) {
      setSelectedKeyword(null);
      return;
    }
    if (!selectedKeyword || !lfScans.find(s => s.keyword === selectedKeyword)) {
      setSelectedKeyword(lfScans[0].keyword);
    }
  }, [lfScans, hasScans, selectedKeyword]);

  // ── Fetch full scan when selectedKeyword changes ────────────────────────
  useEffect(() => {
    if (!selectedKeyword) {
      setFullScan(null);
      return;
    }
    let cancelled = false;
    setScanLoading(true);
    setScanError(null);
    getLfScan(client.id, selectedKeyword)
      .then(scan => { if (!cancelled) setFullScan(scan); })
      .catch(err => { if (!cancelled) setScanError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setScanLoading(false); });
    return () => { cancelled = true; };
  }, [client.id, selectedKeyword]);

  // ── Derived: stats from the selected scan's metadata row ────────────────
  const selectedMeta = useMemo(
    () => lfScans.find(s => s.keyword === selectedKeyword) ?? null,
    [lfScans, selectedKeyword]
  );

  const liveStats = useMemo(() => deriveStats(selectedMeta, data), [selectedMeta, data]);

  // ── Rankings at the clicked point — top-5 + your-area when applicable ──
  const pointRankings = useMemo<PointRankings | null>(() => {
    if (!fullScan || !clickedPoint) return null;
    const point = fullScan.points[clickedPoint.index];
    if (!point) return null;
    return buildPointRankings(point, client.gbpPlaceId, 2);
  }, [fullScan, clickedPoint, client.gbpPlaceId]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleSyncAll = async () => {
    if (!onRefreshLfAll || refreshingAll) return;
    setTopLevelError(null);
    setRefreshingAll(true);
    try { await onRefreshLfAll(); }
    catch (err) { setTopLevelError(err instanceof Error ? err.message : String(err)); }
    finally { setRefreshingAll(false); }
  };

  const handleSyncKeyword = async (kw: string) => {
    if (!onRefreshLfKeyword || refreshingKw) return;
    setTopLevelError(null);
    setRefreshingKw(kw);
    try { await onRefreshLfKeyword(kw); }
    catch (err) { setTopLevelError(err instanceof Error ? err.message : String(err)); }
    finally { setRefreshingKw(null); }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 14 : 28, background: '#fff', position: 'relative' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 16 }}>
        <div>
          <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 22, fontWeight: 700, color: T.fgPrimary, marginBottom: 4 }}>
            Geographic Heatmap
            {hasScans && <GBIBadge color={T.cyan} style={{ marginLeft: 10, fontSize: 10, verticalAlign: 'middle' }}>Live data</GBIBadge>}
          </div>
          <div style={{ fontSize: 13, color: T.fgSecondary }}>
            {client.name} · {client.location}
            {selectedMeta && <> · {selectedMeta.gridSize}×{selectedMeta.gridSize} grid · scanned {new Date(selectedMeta.scanDate).toLocaleDateString()}</>}
          </div>
        </div>
        {!readOnly && onRefreshLfAll && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <GBIBtn
              variant="ghost"
              size="sm"
              onClick={() => setManageOpen(true)}
            >
              ⚙ Manage Keywords
            </GBIBtn>
            <GBIBtn
              variant="secondary"
              size="sm"
              onClick={refreshingAll ? () => {} : handleSyncAll}
              style={{ opacity: refreshingAll ? 0.5 : 1, cursor: refreshingAll ? 'wait' : 'pointer' }}
            >
              {refreshingAll ? 'Refreshing…' : '↻ Refresh heatmap results'}
            </GBIBtn>
            {selectedMeta?.pdfUrl && (
              <a href={selectedMeta.pdfUrl} target="_blank" rel="noopener noreferrer">
                <GBIBtn variant="ghost" size="sm">Open PDF</GBIBtn>
              </a>
            )}
          </div>
        )}
      </div>

      {manageOpen && (
        <ManageKeywordsModal
          clientId={client.id}
          scans={lfScans}
          onClose={() => setManageOpen(false)}
          onChanged={async () => {
            // Re-pull fresh scans by triggering the parent's sync. The parent
            // owns the bundle reload — this is the simplest signal.
            if (onRefreshLfAll) await handleSyncAll();
          }}
        />
      )}

      {topLevelError && (
        <div style={{
          marginBottom: 12, padding: '8px 12px',
          background: 'rgba(232,80,26,0.06)', border: `1px solid ${T.magenta}`, borderRadius: 6,
          fontSize: 12, color: T.magenta, fontFamily: sharedStyles.fontMono,
        }}>{topLevelError}</div>
      )}

      {/* Empty state */}
      {!hasScans && <EmptyState onSync={onRefreshLfAll && !readOnly ? handleSyncAll : undefined} syncing={refreshingAll} />}

      {/* Keyword pills */}
      {hasScans && (
        <KeywordPills
          scans={lfScans}
          selected={selectedKeyword}
          onSelect={setSelectedKeyword}
          onRefreshKeyword={!readOnly && onRefreshLfKeyword ? handleSyncKeyword : undefined}
          refreshing={refreshingKw}
        />
      )}

      {/* Inner tabs — Map (active) + AI Analysis (link to standalone page) */}
      {hasScans && (
        <div style={{
          display: 'flex', gap: 0, borderBottom: `1px solid ${T.border}`,
          marginBottom: 18,
        }}>
          <button
            onClick={() => setActiveTab('map')}
            style={{
              background: 'transparent', border: 'none',
              padding: '10px 18px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
              color: activeTab === 'map' ? T.cyan : T.fgPrimary,
              borderBottom: activeTab === 'map' ? `2px solid ${T.cyan}` : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            Map
          </button>
          {onNav && (
            <button
              onClick={() => onNav('analysis')}
              style={{
                background: 'transparent', border: 'none',
                padding: '10px 18px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', color: T.fgPrimary,
                borderBottom: '2px solid transparent', marginBottom: -1,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
              title="Open the AI Analysis tab"
            >
              AI Analysis <Icon name="chevronRight" size={12} color={T.fgMuted} />
            </button>
          )}
        </div>
      )}

      {/* Active tab body — only Map renders here. Clicking AI Analysis navigates away. */}
      {hasScans && (
        <MapTab
          client={client}
          data={data}
          stats={liveStats}
          fullScan={fullScan}
          scanLoading={scanLoading}
          scanError={scanError}
          clickedPoint={clickedPoint}
          rankings={pointRankings}
          onMarkerClick={pt => setClickedPoint(pt)}
          onClosePanel={() => setClickedPoint(null)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Empty state — no scans synced yet

function EmptyState({ onSync, syncing }: { onSync?: () => void; syncing: boolean }) {
  return (
    <div style={{
      padding: 40, marginTop: 24,
      background: T.bgSubtle, border: `1px dashed ${T.border}`, borderRadius: 12,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.fgPrimary, marginBottom: 6 }}>
        No keyword scans synced yet
      </div>
      <div style={{ fontSize: 12, color: T.fgSecondary, marginBottom: 16 }}>
        Click below to pull the latest weekly scans for this client's GBP into the dashboard.
      </div>
      {onSync && (
        <GBIBtn
          variant="primary"
          size="sm"
          onClick={syncing ? () => {} : onSync}
          style={{ opacity: syncing ? 0.5 : 1 }}
        >
          {syncing ? 'Refreshing…' : '↻ Refresh heatmap results'}
        </GBIBtn>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Keyword pills

// Vertical, prominent, obviously-clickable keyword selector. Mirrors the
// AI Visibility panel's layout so the whole app has one consistent
// "click a keyword row to switch the data below" pattern. Each row is a
// full-width card with a number badge, SoLV pill, hover lift and a clear
// active state — users were missing that these were interactive when they
// were small horizontal pills.
function KeywordPills({
  scans, selected, onSelect, onRefreshKeyword, refreshing,
}: {
  scans: LfScan[];
  selected: string | null;
  onSelect: (k: string) => void;
  onRefreshKeyword?: (k: string) => void;
  refreshing: string | null;
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: T.fgMuted, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        Tracked keywords
        <span style={{
          fontSize: 9, fontWeight: 600, letterSpacing: 0, textTransform: 'none',
          color: T.cyan, background: 'rgba(18,181,196,0.10)',
          border: `1px solid ${T.cyan}`, borderRadius: 10, padding: '1px 8px',
        }}>👆 click to switch</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {scans.map((s, i) => {
          const active = s.keyword === selected;
          const cov = coverageColor(s.solv);
          const loc = locationColor(s.locationRank);
          const isRefreshing = refreshing === s.keyword;
          return (
            <div
              key={s.keyword}
              onClick={() => onSelect(s.keyword)}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto auto',
                gap: 14, alignItems: 'center',
                padding: '12px 16px',
                borderRadius: 10,
                border: active ? `2px solid ${T.cyan}` : `1px solid ${T.border}`,
                background: active ? 'rgba(18,181,196,0.07)' : '#fff',
                cursor: 'pointer',
                transition: 'all 120ms',
                boxShadow: active ? '0 2px 10px rgba(18,181,196,0.18)' : '0 1px 2px rgba(0,0,0,0.03)',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = T.cyan; e.currentTarget.style.background = T.bgSubtle; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = '#fff'; } }}
            >
              {/* Number badge — signals "these are switchable options 1/2/3" */}
              <div style={{
                width: 24, height: 24, borderRadius: 7,
                background: active ? T.cyan : T.bgSubtle,
                color: active ? '#fff' : T.fgMuted,
                fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: active ? 'none' : `1px solid ${T.border}`,
              }}>{i + 1}</div>

              <span style={{
                fontSize: 14, fontWeight: active ? 700 : 600,
                color: active ? T.fgPrimary : T.fgSecondary,
                fontFamily: sharedStyles.fontDisplay,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {s.keyword}
              </span>

              {/* Location + Coverage chips */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: loc.bg, borderRadius: 999, padding: '4px 10px' }}>
                  <span style={{ fontSize: 11 }}>📍</span>
                  <span style={{ fontSize: 9, color: loc.text, letterSpacing: '0.04em', fontWeight: 700 }}>LOCATION</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: loc.text, fontFamily: sharedStyles.fontMono }}>
                    {s.locationRank && s.locationRank > 0 ? `#${s.locationRank}` : '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: cov.bg, borderRadius: 999, padding: '4px 10px' }}>
                  <span style={{ fontSize: 9, color: cov.text, letterSpacing: '0.04em', fontWeight: 700 }}>COVERAGE</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: cov.text, fontFamily: sharedStyles.fontMono }}>
                    {s.solv != null ? `${s.solv.toFixed(0)}%` : '—'}
                  </span>
                </div>
              </div>

              {onRefreshKeyword ? (
                <button
                  onClick={e => { e.stopPropagation(); onRefreshKeyword(s.keyword); }}
                  title={`Refresh this keyword: ${s.keyword}`}
                  aria-label={`Refresh this keyword: ${s.keyword}`}
                  style={{
                    background: 'transparent', border: `1px solid ${T.border}`,
                    borderRadius: 6, padding: '4px 8px',
                    fontSize: 12, color: T.fgMuted,
                    cursor: isRefreshing ? 'wait' : 'pointer',
                    opacity: isRefreshing ? 0.4 : 1,
                  }}
                >{isRefreshing ? '⏳' : '🔄'}</button>
              ) : <span />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Map tab

interface MapStats {
  arp: number | null;
  atrp: number | null;
  solv: number | null;
  foundIn: number | null;
  total: number;
}

function MapTab({
  client, data, stats, fullScan, scanLoading, scanError,
  clickedPoint, rankings, onMarkerClick, onClosePanel,
}: {
  client: Client;
  data: HeatmapData;
  stats: MapStats;
  fullScan: LfFullScan | null;
  scanLoading: boolean;
  scanError: string | null;
  clickedPoint: ClickedPoint | null;
  rankings: PointRankings | null;
  onMarkerClick: (pt: ClickedPoint) => void;
  onClosePanel: () => void;
}) {
  const blind = stats.foundIn != null ? Math.max(0, stats.total - stats.foundIn) : null;
  const panelOpen = clickedPoint != null;
  const isMobile = useIsMobile();

  return (
    <>
      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        <StatCard label="Avg Rank Position" value={stats.arp != null ? stats.arp.toFixed(2) : '—'} color="#188038" sub={`Across ${stats.total} points`} />
        <StatCard label="Share of Local Voice" value={stats.solv != null ? `${stats.solv.toFixed(1)}%` : '—'} color={T.cyan} sub="In Map Pack" />
        <StatCard label="Found In" value={stats.foundIn != null ? `${stats.foundIn}/${stats.total}` : '—'} color={T.yellow} sub={stats.foundIn != null ? `${Math.round((stats.foundIn / stats.total) * 100)}% coverage` : '—'} />
        <StatCard label="Blind Spots" value={blind != null ? String(blind) : '—'} color={T.magenta} sub="Not in Map Pack" />
      </div>

      {scanError && (
        <div style={{
          marginBottom: 12, padding: '8px 12px',
          background: 'rgba(232,80,26,0.06)', border: `1px solid ${T.magenta}`, borderRadius: 6,
          fontSize: 12, color: T.magenta, fontFamily: sharedStyles.fontMono,
        }}>Failed to load scan: {scanError}</div>
      )}

      {/* Side-by-side: map fills remaining width; rankings panel anchors right.
          On phones they stack so the map keeps full width. */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: isMobile ? 'stretch' : 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, position: 'relative', borderRadius: 12, overflow: 'hidden', border: `1px solid ${T.border}`, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
          <LeafletGrid
            client={client}
            data={data}
            fullScan={fullScan}
            panelOpen={panelOpen}
            onMarkerClick={onMarkerClick}
          />
          {scanLoading && (
            <div style={{
              position: 'absolute', top: 12, right: 12, padding: '6px 10px',
              background: 'rgba(255,255,255,0.95)', border: `1px solid ${T.border}`, borderRadius: 6,
              fontSize: 11, color: T.fgSecondary, zIndex: 500,
            }}>Loading scan…</div>
          )}
          <div style={{ padding: '12px 16px', background: '#f8f9fa', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {[
              { color: '#188038', label: '#1 (top)' },
              { color: '#34a853', label: '#2–3' },
              { color: '#fbbc04', label: '#4–7' },
              { color: '#e37400', label: '#8–9' },
              { color: '#d93025', label: '10+ (weak)' },
              { color: '#9aa0a6', label: 'Not found' },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.fgMuted }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: l.color }} />
                {l.label}
              </div>
            ))}
            <div style={{ marginLeft: 'auto', fontSize: 11, color: T.fgMuted, fontStyle: 'italic' }}>
              {panelOpen ? 'Click another point to update the panel' : 'Click any point for ranked businesses'}
            </div>
          </div>
        </div>

        {panelOpen && clickedPoint && (
          <RankingsPanel
            point={clickedPoint}
            rankings={rankings}
            loading={scanLoading}
            onClose={onClosePanel}
          />
        )}
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Stat card primitive

function StatCard({ label, value, color, sub }: { label: string; value: string; color: string; sub: string }) {
  return (
    <div style={{ background: '#f8f9fa', border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ fontSize: 10, color: T.fgMuted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: sharedStyles.fontMono, fontSize: 24, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Leaflet grid — renders one marker per grid point.
// Pulls real ranks from fullScan when present; falls back to bundle.heatmap.grid.

function LeafletGrid({
  client, data, fullScan, panelOpen, onMarkerClick,
}: {
  client: Client;
  data: HeatmapData;
  fullScan: LfFullScan | null;
  panelOpen: boolean;
  onMarkerClick: (pt: ClickedPoint) => void;
}) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<L.Map | null>(null);

  // When the rankings panel opens/closes, the map's container reflows. Leaflet
  // doesn't auto-detect resize — we have to nudge it explicitly, otherwise the
  // tiles/markers stay misaligned to the new bounds.
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map) return;
    // Wait one frame so flex layout settles before measuring.
    const id = requestAnimationFrame(() => map.invalidateSize());
    return () => cancelAnimationFrame(id);
  }, [panelOpen]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Choose center: prefer the scan's center, fall back to bundle.heatmap.
    const centerLat = fullScan?.centerLat || data.centerLat || client.lat || 0;
    const centerLng = fullScan?.centerLng || data.centerLng || client.lng || 0;

    // Tear down any prior map (e.g. when keyword changes).
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
    }

    const map = L.map(mapContainerRef.current, {
      center: [centerLat, centerLng],
      zoom: 11,
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    leafletMapRef.current = map;

    // Build the marker list.
    const markers = buildMarkerList({ data, fullScan });

    const bounds = L.latLngBounds([]);
    markers.forEach(m => {
      const rc = rankColor(m.rank);
      const isTop = m.rank === 1;
      const html = `
        <div style="
          width:32px;height:32px;border-radius:50%;
          background:${m.rank > 0 ? rc.bg : '#9aa0a6'};
          display:flex;align-items:center;justify-content:center;
          font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:700;
          color:${m.rank > 0 ? rc.color : '#fff'};
          box-shadow:${isTop ? '0 0 0 3px #fff,0 0 12px rgba(24,128,56,0.6)' : '0 2px 6px rgba(0,0,0,0.2),0 0 0 2px rgba(255,255,255,0.85)'};
          cursor:pointer;
        ">${m.rank > 20 ? '20+' : m.rank > 0 ? m.rank : 'X'}</div>
      `;
      const icon = L.divIcon({ className: '', html, iconSize: [32, 32], iconAnchor: [16, 16] });
      const marker = L.marker([m.lat, m.lng], { icon }).addTo(map);
      marker.bindTooltip(`#${m.index + 1} · Rank ${m.rank > 0 ? m.rank : 'not found'}`, {
        direction: 'top', offset: [0, -6], className: 'rank-tooltip',
      });
      marker.on('click', () => onMarkerClick({ index: m.index, lat: m.lat, lng: m.lng }));
      bounds.extend([m.lat, m.lng]);
    });
    if (markers.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScan, data.centerLat, data.centerLng, data.rows, data.cols, client.id]);

  return <div ref={mapContainerRef} style={{ height: 480, width: '100%' }} />;
}

// ──────────────────────────────────────────────────────────────────────────
// Right-side rankings panel — sibling to the map, doesn't cover it.

function RankingsPanel({
  point, rankings, loading, onClose,
}: {
  point: ClickedPoint;
  rankings: PointRankings | null;
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <div style={{
      width: 380, maxWidth: '100%', flexShrink: 0,
      background: '#fff',
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
      maxHeight: 540,
      display: 'flex', flexDirection: 'column',
      animation: 'gbi-panel-slide-in 180ms ease-out',
    }}>
      <style>{`@keyframes gbi-panel-slide-in { from { transform: translateX(12px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        padding: '14px 16px', borderBottom: `1px solid ${T.border}`,
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.fgPrimary }}>
            Rankings at point #{point.index + 1}
          </div>
          <div style={{ fontSize: 11, color: T.fgMuted, fontFamily: sharedStyles.fontMono, marginTop: 2 }}>
            {point.lat.toFixed(5)}, {point.lng.toFixed(5)}
          </div>
        </div>
        <button onClick={onClose} title="Close" style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 4, color: T.fgMuted, fontSize: 16, lineHeight: 1,
        }}>✕</button>
      </div>

      <div style={{ padding: '14px 16px', overflowY: 'auto', flex: 1 }}>
        {loading && (
          <div style={{ fontSize: 12, color: T.fgMuted, padding: '20px 0' }}>Loading rankings…</div>
        )}
        {!loading && (!rankings || rankings.topFive.length === 0) && (
          <div style={{ fontSize: 12, color: T.fgMuted, padding: '20px 0' }}>
            No business ranked in the top 20 at this point.
          </div>
        )}
        {!loading && rankings && rankings.topFive.length > 0 && (
          <>
            <SectionHeader>Top {rankings.topFive.length} in Map Pack</SectionHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: rankings.yourArea.length > 0 || rankings.notFound ? 16 : 0 }}>
              {rankings.topFive.map(r => <RankingRow key={r.placeId} ranking={r} />)}
            </div>

            {rankings.yourArea.length > 0 && (
              <>
                <SectionHeader>Your area · ranks {rankings.yourArea[0].rank}–{rankings.yourArea[rankings.yourArea.length - 1].rank}</SectionHeader>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rankings.yourArea.map(r => <RankingRow key={r.placeId} ranking={r} />)}
                </div>
              </>
            )}

            {rankings.notFound && (
              <div style={{
                marginTop: 8, padding: '10px 12px',
                background: 'rgba(232,80,26,0.06)', border: `1px solid ${T.magenta}`, borderRadius: 8,
                fontSize: 12, color: T.magenta,
              }}>
                You are not in the top 20 at this point.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: T.fgMuted,
      marginBottom: 8,
    }}>{children}</div>
  );
}

function RankingRow({ ranking: r }: { ranking: LfRanking }) {
  const rc = rankColor(r.rank);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 12px',
      background: r.isYou ? 'rgba(18,181,196,0.06)' : T.bgSubtle,
      border: `1px solid ${r.isYou ? T.cyan : T.border}`,
      borderRadius: 10,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%', background: rc.bg, color: rc.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: sharedStyles.fontMono, fontSize: 14, fontWeight: 700,
        flexShrink: 0,
      }}>{r.rank}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.fgPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
          {r.isYou && <GBIBadge color={T.cyan} style={{ fontSize: 9, padding: '1px 6px' }}>YOU</GBIBadge>}
        </div>
        <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>
          {r.rating != null && <>★ {r.rating.toFixed(1)}{r.reviews != null ? ` (${r.reviews})` : ''} · </>}
          {r.category ?? '—'}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers

interface MarkerSpec { index: number; lat: number; lng: number; rank: number }

function buildMarkerList({
  data, fullScan,
}: {
  data: HeatmapData;
  fullScan: LfFullScan | null;
}): MarkerSpec[] {
  // Real LF data path — one marker per grid point with the user's actual rank.
  if (fullScan && fullScan.points.length > 0) {
    return fullScan.points.map(p => ({
      index: p.index,
      lat: p.lat,
      lng: p.lng,
      rank: p.myRank, // 0 = not found
    }));
  }
  // Fallback — synthesise a grid around the bundle.heatmap centre.
  const { rows, cols, spacing, centerLat, centerLng, grid } = data;
  const startLat = centerLat + (rows / 2) * spacing;
  const startLng = centerLng - (cols / 2) * spacing;
  const out: MarkerSpec[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      out.push({
        index: r * cols + c,
        lat: startLat - r * spacing,
        lng: startLng + c * spacing,
        rank: grid?.[r]?.[c] ?? 0,
      });
    }
  }
  return out;
}

function deriveStats(meta: LfScan | null, fallback: HeatmapData): MapStats {
  if (meta) {
    const size = meta.gridSize || 7;
    return {
      arp: meta.arp,
      atrp: meta.atrp,
      solv: meta.solv,
      foundIn: meta.foundIn,
      total: size * size,
    };
  }
  // Fallback — extract numeric stats from HeatmapData.stats[] when no LF meta.
  const findStat = (label: string) => fallback.stats.find(s => s.label === label)?.value ?? '—';
  const arpStr = findStat('Avg Rank Position') || findStat('Avg Rank');
  const solvStr = findStat('Share of Local Voice');
  const arp = arpStr === '—' ? null : Number(arpStr);
  const solv = solvStr === '—' ? null : Number(String(solvStr).replace('%', ''));
  return {
    arp: Number.isFinite(arp ?? NaN) ? arp : null,
    atrp: null,
    solv: Number.isFinite(solv ?? NaN) ? solv : null,
    foundIn: null,
    total: fallback.rows * fallback.cols,
  };
}

// Coverage (SoLV) chip — how wide the client's top-3 visibility spreads across the grid.
function coverageColor(solv: number | null): { bg: string; text: string } {
  if (solv == null) return { bg: '#F1F3F4', text: '#80868B' }; // no data — neutral
  if (solv >= 50) return { bg: '#E6F4EA', text: '#188038' };   // strong
  if (solv >= 20) return { bg: '#FEF7E0', text: '#B26A00' };   // moderate
  return { bg: '#FCE8E6', text: '#C5221F' };                    // needs work
}

// Location chip — the client's rank at the grid center point (their own address).
function locationColor(rank: number | null): { bg: string; text: string } {
  if (rank == null || rank <= 0) return { bg: '#FCE8E6', text: '#C5221F' }; // not found
  if (rank <= 3) return { bg: '#E6F4EA', text: '#188038' };  // strong
  if (rank <= 10) return { bg: '#FEF7E0', text: '#B26A00' }; // moderate
  return { bg: '#FCE8E6', text: '#C5221F' };                  // needs work
}

// Sections shown in the rankings panel for one clicked grid point.
interface PointRankings {
  topFive: LfRanking[];      // up to 5 rows
  yourArea: LfRanking[];     // user ± neighbors when user is outside top 5; empty otherwise
  notFound: boolean;         // true when user has no rank at this point
}

// Compute the panel content for one grid point.
//   - topFive: results[0..4]
//   - yourArea: when user is outside top 5 AND found, the user's row plus
//     `neighborWindow` rows above and below (clamped to results).
//   - notFound: true when user isn't in results at all (rank = 0 / missing).
function buildPointRankings(
  point: LfGridPoint,
  myPlaceId: string | undefined,
  neighborWindow: number,
): PointRankings {
  const all = point.results;
  const topFive = all.slice(0, 5);

  if (!myPlaceId) {
    return { topFive, yourArea: [], notFound: !point.found };
  }
  const myIdx = all.findIndex(r => r.placeId === myPlaceId);
  if (myIdx === -1) {
    return { topFive, yourArea: [], notFound: true };
  }
  if (myIdx < 5) {
    // User is already in topFive — the YOU badge is enough; no extra section.
    return { topFive, yourArea: [], notFound: false };
  }
  const start = Math.max(5, myIdx - neighborWindow);
  const end = Math.min(all.length, myIdx + neighborWindow + 1);
  return { topFive, yourArea: all.slice(start, end), notFound: false };
}

// ──────────────────────────────────────────────────────────────────────────
// Manage Keywords modal — delete stale/dirty rows that LF still has campaigns
// for. The user removes the campaign in Local Falcon to stop it re-syncing.
// We only DELETE rows here; adding new keywords happens in Local Falcon itself.

// Invisible-character ranges used to spot keyword strings corrupted by
// copy-paste from rich-text (zero-width spaces, word joiners, BOM, NBSP).
// Kept as a module constant so the file parses cleanly without dirty literals.
const INVISIBLE_RE = /[\u00A0\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/g;
const INVISIBLE_DETECT_RE = /[\u00A0\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/;

function ManageKeywordsModal({
  clientId, scans, onClose, onChanged,
}: {
  clientId: string;
  scans: LfScan[];
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async (keyword: string) => {
    if (busy) return;
    const visible = keyword.replace(INVISIBLE_RE, '·');
    if (!confirm(`Delete the scan row for "${visible}"? This only removes it from GBI — to stop it from syncing back, also remove the campaign in Local Falcon.`)) return;
    setBusy(keyword);
    setError(null);
    try {
      await deleteLfScan(clientId, keyword);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  // Highlight any keyword containing invisible characters (zero-width, BOM, etc).
  // These almost always indicate copy-paste corruption from rich-text and are
  // the typical "duplicate keyword" root cause.
  const DIRTY_RE = INVISIBLE_DETECT_RE;

  return (
    <div onClick={busy ? undefined : onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 540, maxWidth: '92vw',
        maxHeight: '85vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 18, fontWeight: 700, color: T.fgPrimary, marginBottom: 6 }}>
          Manage Maps Keywords
        </div>
        <div style={{ fontSize: 12, color: T.fgSecondary, marginBottom: 16, lineHeight: 1.5 }}>
          Source of truth lives in Local Falcon. <strong>Add or rename keywords there</strong>, then come back and refresh the heatmap results.
          Use this list to remove stale rows or scrub a typo (look for the ⚠ marker — usually a hidden Unicode character from copy-paste).
        </div>

        {scans.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>
            No Maps scans synced yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {scans.map(s => {
              const dirty = DIRTY_RE.test(s.keyword);
              return (
                <div key={s.keyword} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px',
                  background: dirty ? 'rgba(245,160,32,0.08)' : T.bgSubtle,
                  border: `1px solid ${dirty ? T.amber : T.border}`,
                  borderRadius: 6,
                }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{
                      fontFamily: sharedStyles.fontMono, fontSize: 12, color: T.fgPrimary,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {dirty && <span title="Contains invisible Unicode characters" style={{ color: T.amber, marginRight: 6 }}>⚠</span>}
                      {s.keyword.replace(INVISIBLE_RE, '·')}
                    </div>
                    <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 2 }}>
                      {s.gridSize}×{s.gridSize} · SoLV {s.solv?.toFixed(1) ?? '—'}% · scanned {new Date(s.scanDate).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(s.keyword)}
                    disabled={busy !== null}
                    style={{
                      background: 'transparent', border: `1px solid ${T.magenta}`,
                      color: T.magenta, borderRadius: 4, padding: '4px 10px',
                      fontSize: 11, fontWeight: 600,
                      cursor: busy ? 'wait' : 'pointer',
                      opacity: busy === s.keyword ? 0.5 : 1,
                    }}
                  >
                    {busy === s.keyword ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <div style={{
            marginTop: 14, padding: '10px 12px',
            background: 'rgba(232,80,26,0.08)', border: '1px solid #E8501A',
            borderRadius: 6, fontSize: 12, color: '#C03B0F',
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <GBIBtn variant="primary" onClick={onClose} disabled={busy !== null}>Done</GBIBtn>
        </div>
      </div>
    </div>
  );
}
